import {MessageReaction} from './messenger.models';

/**
 * THE REACTIONS A MEMBER CAN ADD (ITP-E-194-05). A reaction is today's `AddEmotionCommand`. The picker offers six glyphs
 * across, as carbon's reaction bar does, each of which the server's fixed `EmotionType` set can carry; with B-10 live
 * (ITP-E-207-04) it also takes any emoji, which goes to the server as the command's `emoji`.
 */
export interface ReactionChoice {
  glyph: string;
  name: string;
  /** The server's `EmotionType` (`Orbitax.Collaboration.Contracts.Models.EmotionType`). */
  emotionType: number;
}

/** Every `EmotionType` as the glyph it is drawn with; the server reads a reaction stored before B-10 as the same glyph. */
export const EMOTION_GLYPHS: Readonly<Record<number, string>> = {
  0: '👍',  // Like
  1: '👎',  // Dislike
  2: '❤️',  // Love
  3: '💔',  // Hate
  4: '😠',  // Angry
  5: '😢',  // Sad
  6: '⬆️',  // UpVote
  7: '⬇️',  // DownVote
  8: '👍',  // ThumsUp
  9: '👎',  // ThumsDown
  10: '⭐', // Star
  11: '😞'  // Disappointed
};

/** The `EmotionType` names, by value, as an event may carry them. */
const EMOTION_TYPE_NAMES = ['Like', 'Dislike', 'Love', 'Hate', 'Angry', 'Sad', 'UpVote', 'DownVote', 'ThumsUp', 'ThumsDown', 'Star',
  'Disappointed'];

export const REACTION_CHOICES: readonly ReactionChoice[] = [
  {glyph: '👍', name: 'Thumbs up', emotionType: 8},
  {glyph: '❤️', name: 'Love', emotionType: 2},
  {glyph: '⭐', name: 'Star', emotionType: 10},
  {glyph: '😢', name: 'Sad', emotionType: 5},
  {glyph: '😠', name: 'Angry', emotionType: 4},
  {glyph: '👎', name: 'Thumbs down', emotionType: 9}
];

/** The text and emoji presentation selectors (U+FE0E, U+FE0F): one emoji with or without them is one reaction. */
const PRESENTATION_SELECTORS = new RegExp(`[${String.fromCharCode(0xFE0E, 0xFE0F)}]`, 'g');

/** The `EmotionType` a glyph is sent as; null for a glyph the server cannot carry. */
export function emotionTypeOf(glyph: string): number | null {
  return REACTION_CHOICES.find(choice => sameEmoji(choice.glyph, glyph))?.emotionType ?? null;
}

/** Whether two emoji are one reaction, as the server groups them: ❤ and ❤️ are. */
export function sameEmoji(a: string, b: string): boolean {
  return !!a && !!b && a.replace(PRESENTATION_SELECTORS, '') === b.replace(PRESENTATION_SELECTORS, '');
}

/** A person who reacted, as `MessageReaction.by` lists them. */
export interface Reactor {
  email: string;
  name: string;
}

/** A reaction added or taken back by someone, as its live event says (B-10). */
export interface ReactionChange extends Reactor {
  messageId: string;
  emoji: string;
  added: boolean;
}

/**
 * The reactions under a message once `person` has added (`added`) or taken back their `emoji`: its count, `mine` (when the
 * person is the reader, `isReader`) and `by` follow, an emoji nobody holds any more goes, a new one goes last. Applying the
 * same change twice changes nothing.
 */
export function withReaction(reactions: MessageReaction[], emoji: string, person: Reactor, added: boolean,
                             isReader: boolean): MessageReaction[] {
  const current = reactions || [];
  const email = (person.email || '').toLowerCase();
  const index = current.findIndex(reaction => sameEmoji(reaction.emoji, emoji));
  const existing = index < 0 ? null : current[index];
  const holds = !!existing && (existing.by
    ? existing.by.some(reactor => reactor.email?.toLowerCase() === email)
    : isReader && existing.mine);
  if (added === holds) {
    return current;
  }
  const reactor = {email, name: person.name || email};
  if (added) {
    return existing
      ? current.map((reaction, at) => at !== index ? reaction : {
        ...reaction,
        count: reaction.count + 1,
        mine: reaction.mine || isReader,
        by: reaction.by ? [...reaction.by, reactor] : undefined
      })
      : [...current, {emoji, count: 1, mine: isReader, by: [reactor]}];
  }
  return existing.count <= 1
    ? current.filter((_, at) => at !== index)
    : current.map((reaction, at) => at !== index ? reaction : {
      ...reaction,
      count: reaction.count - 1,
      mine: isReader ? false : reaction.mine,
      by: reaction.by?.filter(other => other.email?.toLowerCase() !== email)
    });
}

/**
 * B-10 (ITP-E-207-04): the change a live reaction event makes. `AddEmotion` carries the stored reaction (its `emoji`, or for
 * an add without one the glyph of its `emotionType`, and its user); `RemoveEmotion` names the message, the emoji and who took
 * it back. The server may camel- or Pascal-case either. Null for anything else, or an event missing what it needs.
 */
export function reactionChangeOf(eventType: string, payload: any): ReactionChange | null {
  if (!payload || typeof payload !== 'object') {
    return null;
  }
  const read = (camel: string, pascal: string) => payload[camel] ?? payload[pascal];
  let change: ReactionChange;
  if (eventType === 'AddEmotion') {
    const user = read('user', 'User');
    const firstName = user?.firstName ?? user?.FirstName ?? '';
    const lastName = user?.lastName ?? user?.LastName ?? '';
    change = {
      messageId: read('itemId', 'ItemId'),
      emoji: read('emoji', 'Emoji') || glyphOfEmotionType(read('emotionType', 'EmotionType')),
      email: user?.email ?? user?.Email,
      name: `${firstName} ${lastName}`.trim(),
      added: true
    };
  } else if (eventType === 'RemoveEmotion') {
    change = {
      messageId: read('messageId', 'MessageId'),
      emoji: read('emoji', 'Emoji'),
      email: read('email', 'Email'),
      name: read('name', 'Name'),
      added: false
    };
  } else {
    return null;
  }
  if (!change.messageId || !change.emoji || !change.email) {
    return null;
  }
  return {...change, email: change.email.toLowerCase(), name: change.name || change.email};
}

/** The chip's tooltip: who reacted, by name, the reader as "You"; empty when the server named nobody. */
export function reactedByLine(reaction: MessageReaction, readerEmail: string): string {
  const reader = (readerEmail || '').toLowerCase();
  return (reaction?.by || [])
    .map(reactor => reader && reactor.email?.toLowerCase() === reader ? 'You' : reactor.name || reactor.email)
    .join(', ');
}

/** The glyph an `EmotionType` is drawn with, by value or by name. */
function glyphOfEmotionType(type: number | string): string | null {
  const value = typeof type === 'string' && isNaN(Number(type)) ? EMOTION_TYPE_NAMES.indexOf(type) : Number(type);
  return EMOTION_GLYPHS[value] ?? null;
}
